/**
 * @fileoverview Search and link UI for joining an existing companion group.
 *
 * Uses MatterSelectorCombobox filtered to the firm, with validation to prevent
 * linking to matters that are already in another companion group.
 *
 * The component manages its own selected matter state internally and passes
 * both matterId and matterName to the parent when the user clicks Link.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { Link2 } from 'lucide-react';
import { useState } from 'react';

import { MatterSelectorCombobox } from '@/components/matter/MatterSelectorCombobox';
import { Button } from '@/components/ui/button';
import { useGetMattersQuery } from '@/services/api/mattersService';

interface LinkToExistingGroupProps {
  firmId: string | null;
  currentMatterId: string;
  /** Called when target is already a primary - direct add to their group */
  onLinkToPrimary: (primaryMatterId: string) => void;
  /** Called when target is standalone - show primary picker dialog */
  onLinkWithPicker: (targetMatterId: string, targetMatterName: string) => void;
  isLinking: boolean;
}

export const LinkToExistingGroup: React.FC<LinkToExistingGroupProps> = ({
  firmId,
  currentMatterId,
  onLinkToPrimary,
  onLinkWithPicker,
  isLinking,
}) => {
  const [selectedMatterId, setSelectedMatterId] = useState('');

  // Fetch matters to look up the selected matter's info
  const { data: mattersData } = useGetMattersQuery(
    firmId ? { firm: [firmId] } : skipToken,
  );

  const isValidSelection = selectedMatterId && selectedMatterId !== currentMatterId;

  const handleLinkClick = () => {
    if (!isValidSelection) return;

    const selectedMatter = mattersData?.results?.find((m) => m.id === selectedMatterId);

    // If target is already a primary, add directly to their group
    if (selectedMatter?.is_companion_primary) {
      onLinkToPrimary(selectedMatterId);
    } else {
      // Target is standalone - show primary picker
      const matterName = selectedMatter?.name ?? `Matter ${selectedMatterId.slice(0, 8)}`;
      onLinkWithPicker(selectedMatterId, matterName);
    }
  };

  return (
    <div className="rounded-md border border-zinc-200 p-2 space-y-2 bg-white">
      <div className="flex items-center gap-1.5">
        <Link2 className="h-3.5 w-3.5 text-zinc-600" />
        <p className="text-xs font-medium text-zinc-900">Search for the case to link:</p>
      </div>

      <MatterSelectorCombobox
        value={selectedMatterId}
        onChange={setSelectedMatterId}
        includeAllOption={false}
        placeholder="Search cases by name or ID"
        firmId={firmId ?? undefined}
        excludeFilter={(m) =>
          // Hide non-primary members of companion groups (can't link to them)
          // Primary matters are shown - user can join their existing group
          (Boolean(m.companion_root_id) && !m.is_companion_primary) ||
          // Hide self
          m.id === currentMatterId
        }
      />

      <Button
        type="button"
        onClick={handleLinkClick}
        disabled={!isValidSelection || isLinking}
        className="w-full h-7 text-xs"
      >
        {isLinking ? 'Linking...' : 'Link to Case'}
      </Button>
    </div>
  );
};
